import React, { useState, useEffect, useCallback, useRef, Fragment } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  PhoneIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentIcon,
  MusicalNoteIcon,
  EyeIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  PlayIcon,
  PauseIcon,
} from "@heroicons/react/24/outline";
import {
  StopIcon as StopIconSolid
} from "@heroicons/react/24/solid";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Card, Button, Tag } from "components/ui";
import { toast } from "sonner";
import { getCommunicationTimeline } from "services/leadsService";
import { formatDisplayDate, getLocalDateTime } from "../../../utils/dateUtils";
import WhatsAppPreview from "../../../app/pages/settings/sections/TemplateManagement/modals/WhatsAppPreview";
import axiosInstance from "../../../utils/axios";
import { ensureCompleteEmailHtml, isCompleteHtmlDocument as _isCompleteEmailHtml } from "utils/emailHtmlWrapper";
import { getDocumentUrl } from "./DocumentsTab/utils/documentUtils";

const CHANNEL_TABS = [
  { id: 0, name: "All", icon: InformationCircleIcon, channelId: 0 },
  { id: 4, name: "Call", icon: PhoneIcon, channelId: 4 },
  { id: 1, name: "Email", icon: EnvelopeIcon, channelId: 1 },
  { id: 2, name: "SMS", icon: PaperAirplaneIcon, channelId: 2 },
  { id: 3, name: "WhatsApp", icon: ChatBubbleLeftRightIcon, channelId: 3 },
];

const COMMUNICATION_STATUS = {
  0: { label: "Pending", color: "warning", icon: ClockIcon },
  1: { label: "Received", color: "info", icon: ArrowPathIcon },
  2: { label: "Sent", color: "success", icon: CheckCircleIcon },
  3: { label: "Failed", color: "error", icon: XCircleIcon },
  5: { label: "Failed", color: "error", icon: XCircleIcon },
};

// Media Viewer Modal Component
const MediaViewerModal = ({ isOpen, onClose, mediaPreview, caption }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileName = mediaPreview?.FileName || 'Media file';
  const mediaType = mediaPreview?.MediaType?.toLowerCase();
  const fileExtension = mediaPreview?.FilePath?.split('.').pop()?.toLowerCase();

  // Build media URL using axiosInstance base URL
  // const baseURL = axiosInstance.defaults.baseURL || 'http://localhost:5096';
  // const cleanFilePath = mediaPreview?.FilePath?.replace(/^\//, '') || '';
  // const mediaUrl = `${baseURL}/${cleanFilePath}`;
  const mediaUrl = getDocumentUrl(mediaPreview?.FilePath);

  // Determine media type
  const isImage = mediaType === 'image' || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension);
  const isVideo = mediaType === 'video' || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(fileExtension);
  const isAudio = mediaType === 'audio' || ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(fileExtension);
  const isDocument = mediaType === 'document' || ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(fileExtension);

  // For documents, set loading to false immediately since they don't have onLoad events
  useEffect(() => {
    if (isDocument) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [isDocument]);


  if (!mediaPreview) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-100 flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity dark:bg-black/40" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-4xl origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700">
            {/* Header */}
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <div className="flex-1 min-w-0">
                <DialogTitle
                  as="h3"
                  className="text-base font-medium text-gray-800 dark:text-dark-100 truncate"
                >
                  {fileName}
                </DialogTitle>
                {caption && (
                  <p className="mt-0.5 text-sm text-gray-600 dark:text-dark-400 truncate">
                    {caption}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ltr:ml-2 rtl:mr-2">
                {/* Show action buttons only for images and videos */}
                {(isImage || isVideo) && (
                  <>
                    {/* Open in new tab */}
                    <Button
                      onClick={() => window.open(mediaUrl, '_blank', 'noopener,noreferrer')}
                      variant="flat"
                      isIcon
                      className="size-7 rounded-full"
                      title="Open in new tab"
                    >
                      <ArrowTopRightOnSquareIcon className="size-4.5" />
                    </Button>
                  </>
                )}
                {/* Close button */}
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full"
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>
            </div>

            {/* Media Content */}
            <div className="scrollbar-sm flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
              {loading && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600 dark:border-dark-600 dark:border-t-primary-400"></div>
                  <p className="mt-4 text-sm text-gray-500 dark:text-dark-400">Loading media...</p>
                </div>
              )}

              {error && (
                <div className="flex flex-col items-center justify-center py-12 text-error">
                  <PhotoIcon className="h-16 w-16 mb-4" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <div className={loading || error ? 'hidden' : ''}>
                {isImage && (
                  <div className="flex justify-center">
                    <img
                      src={mediaUrl}
                      alt={fileName}
                      className="max-w-full max-h-[70vh] rounded-lg object-contain"
                      onLoad={() => setLoading(false)}
                      onError={() => {
                        setLoading(false);
                        setError('Failed to load image');
                      }}
                    />
                  </div>
                )}

                {isVideo && (
                  <div className="flex justify-center">
                    <video
                      controls
                      className="max-w-full max-h-[70vh] rounded-lg"
                      style={{ objectFit: 'contain' }}
                      onLoadedData={() => setLoading(false)}
                      onError={() => {
                        setLoading(false);
                        setError('Failed to load video');
                      }}
                    >
                      <source src={mediaUrl} type={`video/${fileExtension}`} />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {isAudio && (
                  <div className="flex flex-col items-center py-8">
                    <MusicalNoteIcon className="h-16 w-16 text-gray-400 dark:text-dark-400 mb-4" />
                    <audio
                      controls
                      className="w-full max-w-md"
                      onLoadedData={() => setLoading(false)}
                      onError={() => {
                        setLoading(false);
                        setError('Failed to load audio');
                      }}
                    >
                      <source src={mediaUrl} type={`audio/${fileExtension}`} />
                      Your browser does not support the audio tag.
                    </audio>
                  </div>
                )}

                {isDocument && (
                  <div className="flex flex-col items-center py-12">
                    <DocumentIcon className="h-16 w-16 text-primary-500 mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-dark-100 mb-2">
                      {fileName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-dark-400 mb-6">
                      {fileExtension?.toUpperCase()} Document
                    </p>
                    <Button
                      onClick={() => window.open(mediaUrl, '_blank', 'noopener,noreferrer')}
                      color="primary"
                      className="rounded-full"
                    >
                      Open Document
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

// Component to render media message with thumbnail and "View Media" button
const MediaMessage = ({ communication }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Parse MediaPreview JSON
  let mediaPreview = null;
  try {
    mediaPreview = typeof communication.MediaPreview === 'string'
      ? JSON.parse(communication.MediaPreview)
      : communication.MediaPreview;
  } catch (e) {
    console.error('Failed to parse MediaPreview:', e);
  }

  if (!mediaPreview?.FilePath) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        Media file not available
      </div>
    );
  }

  const fileName = mediaPreview.FileName || 'Media file';
  const mediaType = mediaPreview.MediaType?.toLowerCase();
  const fileExtension = mediaPreview.FilePath?.split('.').pop()?.toLowerCase();

  // Build media URL
  // const baseURL = axiosInstance.defaults.baseURL || 'http://localhost:5096';
  // const cleanFilePath = mediaPreview.FilePath.replace(/^\//, '');
  // const mediaUrl = `${baseURL}/${cleanFilePath}`;
  const mediaUrl = getDocumentUrl(mediaPreview.FilePath);

  // Determine media type
  const isImage = mediaType === 'image' || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension);
  const isVideo = mediaType === 'video' || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(fileExtension);
  const isAudio = mediaType === 'audio' || ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(fileExtension);
  const isDocument = mediaType === 'document' || ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(fileExtension);
  const isPDF = fileExtension === 'pdf';
  const isXLSX = ['xls', 'xlsx'].includes(fileExtension);
  // All document types open directly in new tab (can't be displayed in modal)
  const isDirectView = isDocument; // PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, etc.

  // Get appropriate icon
  const MediaIcon = isImage ? PhotoIcon : isVideo ? VideoCameraIcon : isAudio ? MusicalNoteIcon : DocumentIcon;
  const iconColor = isImage ? 'text-green-600 bg-green-100 dark:bg-green-900/20' :
    isVideo ? 'text-purple-600 bg-purple-100 dark:bg-purple-900/20' :
      isAudio ? 'text-orange-600 bg-orange-100 dark:bg-orange-900/20' :
        isPDF ? 'text-red-600 bg-red-100 dark:bg-red-900/20' :
          isXLSX ? 'text-green-700 bg-green-100 dark:bg-green-900/20' :
            'text-blue-600 bg-blue-100 dark:bg-blue-900/20';

  const mediaTypeLabel = isImage ? 'Image' : isVideo ? 'Video' : isAudio ? 'Audio' : 'Document';

  // Handle media click - All documents open directly in new tab, media files (images/videos/audio) open modal
  const handleMediaClick = () => {
    if (isDirectView) {
      window.open(mediaUrl, '_blank', 'noopener,noreferrer');
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <div className="mb-3">
        {/* Media thumbnail card */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${iconColor}`}>
              <MediaIcon className="h-6 w-6" />
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {fileName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {mediaTypeLabel} • {fileExtension?.toUpperCase()}
              </p>
            </div>

            {/* View button */}
            <button
              onClick={handleMediaClick}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            >
              <EyeIcon className="h-4 w-4" />
              View Media
            </button>
          </div>

          {/* Caption (Subject) - shown below the media info */}
          {communication.Subject && communication.Subject.trim() && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                &ldquo;{communication.Subject}&rdquo;
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Media Viewer Modal - Only for media files (images, videos, audio). Documents open directly in new tab. */}
      {!isDirectView && (
        <MediaViewerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          mediaPreview={mediaPreview}
          caption={communication.Subject}
        />
      )}
    </>
  );
};

// Component to render email attachments similar to WhatsApp media
const EmailAttachments = ({ attachments }) => {
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const getAttachmentUrl = (filePath) => {
    return getDocumentUrl(filePath);
  };

  const getFileInfo = (attachment) => {
    const fileExtension = attachment.FileExtension?.trim().toLowerCase().replace('.', '') ||
      attachment.FilePath?.split('.').pop()?.toLowerCase() || '';

    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileExtension);
    const isVideo = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv'].includes(fileExtension);
    const isAudio = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(fileExtension);
    const isPDF = fileExtension === 'pdf';
    const isExcel = ['xls', 'xlsx', 'xlsm', 'xlsb'].includes(fileExtension);
    const isWord = ['doc', 'docx'].includes(fileExtension);
    const isPowerPoint = ['ppt', 'pptx'].includes(fileExtension);
    const isZip = ['zip', 'rar', '7z', 'tar', 'gz'].includes(fileExtension);
    const isExecutable = ['exe', 'msi', 'app', 'dmg'].includes(fileExtension);
    const isDocument = isPDF || isExcel || isWord || isPowerPoint || ['txt', 'csv'].includes(fileExtension);

    let MediaIcon = DocumentIcon;
    let iconColor = 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
    let mediaTypeLabel = 'Document';

    if (isImage) {
      MediaIcon = PhotoIcon;
      iconColor = 'text-green-600 bg-green-100 dark:bg-green-900/20';
      mediaTypeLabel = 'Image';
    } else if (isVideo) {
      MediaIcon = VideoCameraIcon;
      iconColor = 'text-purple-600 bg-purple-100 dark:bg-purple-900/20';
      mediaTypeLabel = 'Video';
    } else if (isAudio) {
      MediaIcon = MusicalNoteIcon;
      iconColor = 'text-orange-600 bg-orange-100 dark:bg-orange-900/20';
      mediaTypeLabel = 'Audio';
    } else if (isPDF) {
      iconColor = 'text-red-600 bg-red-100 dark:bg-red-900/20';
      mediaTypeLabel = 'PDF';
    } else if (isExcel) {
      iconColor = 'text-green-700 bg-green-100 dark:bg-green-900/20';
      mediaTypeLabel = 'Excel';
    } else if (isWord) {
      iconColor = 'text-blue-700 bg-blue-100 dark:bg-blue-900/20';
      mediaTypeLabel = 'Word';
    } else if (isPowerPoint) {
      iconColor = 'text-orange-700 bg-orange-100 dark:bg-orange-900/20';
      mediaTypeLabel = 'PowerPoint';
    } else if (isZip) {
      iconColor = 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
      mediaTypeLabel = 'Archive';
    } else if (isExecutable) {
      iconColor = 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
      mediaTypeLabel = 'Executable';
    }

    return {
      isImage,
      isVideo,
      isAudio,
      isDocument,
      isExecutable,
      MediaIcon,
      iconColor,
      mediaTypeLabel,
      fileExtension
    };
  };

  const handleAttachmentClick = (attachment) => {
    const fileInfo = getFileInfo(attachment);
    const attachmentUrl = getAttachmentUrl(attachment.FilePath);

    // For documents, archives, and executables, open in new tab
    if (fileInfo.isDocument || fileInfo.mediaTypeLabel === 'Archive' || fileInfo.isExecutable) {
      window.open(attachmentUrl, '_blank', 'noopener,noreferrer');
    } else {
      // For images, videos, and audio, open in modal
      setSelectedAttachment({
        ...attachment,
        ...fileInfo,
        url: attachmentUrl
      });
      setIsModalOpen(true);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown size';
    // Backend returns KB values in FileSizeBytes field, not actual bytes
    // So we treat the value as KB directly
    const kb = bytes;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <>
      <div className="mt-3 space-y-2">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
          📎 Attachments ({attachments.length})
        </div>
        {attachments.map((attachment, index) => {
          const fileInfo = getFileInfo(attachment);
          const { MediaIcon, iconColor, mediaTypeLabel, fileExtension } = fileInfo;

          return (
            <div
              key={attachment.AttachmentId || index}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${iconColor}`}>
                  <MediaIcon className="h-5 w-5" />
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {attachment.FileName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {mediaTypeLabel} • {fileExtension.toUpperCase()} • {formatFileSize(attachment.FileSizeBytes)}
                  </p>
                </div>

                {/* View/Download button */}
                <button
                  onClick={() => handleAttachmentClick(attachment)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                >
                  {fileInfo.isDocument || fileInfo.mediaTypeLabel === 'Archive' || fileInfo.isExecutable ? (
                    <>
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      Open
                    </>
                  ) : (
                    <>
                      <EyeIcon className="h-3.5 w-3.5" />
                      View
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Media Viewer Modal for images, videos, and audio */}
      {selectedAttachment && !selectedAttachment.isDocument && selectedAttachment.mediaTypeLabel !== 'Archive' && !selectedAttachment.isExecutable && (
        <MediaViewerModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedAttachment(null);
          }}
          mediaPreview={{
            FileName: selectedAttachment.FileName,
            FilePath: selectedAttachment.FilePath,
            MediaType: selectedAttachment.isImage ? 'image' : selectedAttachment.isVideo ? 'video' : 'audio'
          }}
          caption={null}
        />
      )}
    </>
  );
};

// Component to render Call message with Audio Player
const CallMessage = ({ communication }) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  const formatAudioDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current = null;
    }
    setPlaying(false);
    setPaused(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    if (!playing || !audioRef.current) return;
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, x / width));

    const newTime = percentage * (audioRef.current.duration || 0);
    audioRef.current.currentTime = newTime;
    setProgress(percentage * 100);
    setCurrentTime(newTime);
  };

  const handlePlayPause = async (e) => {
    e.stopPropagation();

    // Resume if paused
    if (playing && paused && audioRef.current) {
      try {
        await audioRef.current.play();
        setPaused(false);
      } catch (err) {
        console.error("Resume error:", err);
        stopAudio();
      }
      return;
    }

    // Pause if playing
    if (playing && !paused && audioRef.current) {
      audioRef.current.pause();
      setPaused(true);
      return;
    }

    // Start new playback
    // Use MessageId or LogId as the call identifier
    const callId = communication.MessageId || communication.LogId;
    if (!callId) {
      toast.error("Call ID not available");
      return;
    }

    try {
      setLoading(true);

      // Determine provider from message text if code is missing
      let provider = communication.ProviderCode;
      if (!provider && communication.Message) {
        if (communication.Message.toLowerCase().includes('ozonetel')) provider = 'OZONETEL';
        if (communication.Message.toLowerCase().includes('twilio')) provider = 'TWILIO';
      }

      const response = await axiosInstance.get(`/api/Voice/recording/${callId}`);

      if (response.data && response.data.success && response.data.data && response.data.data.url) {
        const recordingSid = response.data.data.recordingId;

        let audioUrl = null;

        if (provider === 'OZONETEL') {
          audioUrl = response.data.data.url;
        } else {
          // Twilio/Default Proxy
          const audioResponse = await axiosInstance.get('/api/Voice/play', {
            params: {
              recordingSid: recordingSid || callId,
              provider: provider || 'TWILIO'
            },
            responseType: 'blob'
          });
          const audioBlob = new Blob([audioResponse.data], { type: 'audio/mpeg' });
          audioUrl = URL.createObjectURL(audioBlob);
        }

        if (!audioUrl) throw new Error("Could not result audio URL");

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onloadedmetadata = () => {
          setDuration(audio.duration);
        };

        audio.ontimeupdate = () => {
          setCurrentTime(audio.currentTime);
          setProgress((audio.currentTime / audio.duration) * 100);
        };

        audio.onended = () => {
          // Don't stop entirely to preserve fetched recording
          setPaused(true);
          setCurrentTime(0);
          setProgress(0);
        };

        audio.onerror = (err) => {
          console.error("Audio error", err);
          toast.error("Failed to play audio");
          stopAudio();
        };

        await audio.play();
        setPlaying(true);
        setPaused(false);

      } else {
        toast.error("Recording not available");
      }

    } catch (err) {
      console.error("Playback error:", err);
      toast.error("Failed to load recording");
      stopAudio();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-3">
      {/* Context/Message */}
      <div className="mb-2 text-sm text-gray-700 dark:text-gray-300">
        {communication.Message}
      </div>

      {/* Audio Player UI */}
      <div className="flex items-center gap-2 mt-2">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
            Loading audio...
          </div>
        ) : (
          <>
            {/* Play/Pause Button */}
            <Button
              size="xs"
              variant="ghost"
              onClick={handlePlayPause}
              className={`h-8 w-8 rounded-full p-0 flex items-center justify-center ${playing ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-600'}`}
            >
              {playing && !paused ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
            </Button>

            {/* Expanded Player Controls */}
            {playing && (
              <div className="flex items-center gap-3 flex-1 max-w-md bg-gray-50 dark:bg-gray-800 rounded-full px-3 py-1 border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] font-mono font-medium text-gray-500 min-w-[30px]">
                  {formatAudioDuration(currentTime)}
                </span>

                <div
                  className="relative flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full cursor-pointer group"
                  onClick={handleSeek}
                >
                  <div className="absolute top-0 left-0 h-full bg-primary-500 rounded-full" style={{ width: `${progress}%` }}></div>
                </div>

                <span className="text-[10px] font-mono font-medium text-gray-500 min-w-[30px]">
                  {formatAudioDuration(duration)}
                </span>
              </div>
            )}

            {/* Call Action Button if not playing */}
            {!playing && (
              <span className="text-xs text-gray-400 italic">
                Click play to listen
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Helper function to render WhatsApp template message using WhatsAppPreview component
const renderTemplateMessage = (communication) => {
  // Parse Message field which contains template parts as JSON array
  let templateParts = [];
  try {
    templateParts = typeof communication.Message === 'string'
      ? JSON.parse(communication.Message)
      : communication.Message;
  } catch (e) {
    console.error('Failed to parse template message:', e);
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        Template data not available
      </div>
    );
  }

  // Extract parts from the array - with safety checks for null or non-array data
  if (!templateParts || !Array.isArray(templateParts)) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        Template data not available
      </div>
    );
  }

  const headerPart = templateParts.find(p => p.type === 'header');
  const bodyPart = templateParts.find(p => p.type === 'body');
  const footerPart = templateParts.find(p => p.type === 'footer');
  const buttonsPart = templateParts.find(p => p.type === 'buttons');

  // Parse buttons - the value might be a stringified JSON array
  let buttons = [];
  if (buttonsPart?.value) {
    try {
      if (typeof buttonsPart.value === 'string') {
        buttons = JSON.parse(buttonsPart.value);
      } else if (Array.isArray(buttonsPart.value)) {
        buttons = buttonsPart.value;
      }
    } catch (e) {
      console.error('Failed to parse buttons:', e);
      buttons = [];
    }
  }

  return (
    <div className="mb-3">
      {/* WhatsApp Preview Component */}
      <div className="max-w-sm">
        <WhatsAppPreview
          headerText={headerPart?.value || ''}
          headerFormat="TEXT"
          bodyText={bodyPart?.value || ''}
          footerText={footerPart?.value || ''}
          buttons={buttons}
          mediaUrl={null}
        />
      </div>
    </div>
  );
};

// Helper function to render normal text message
const renderTextMessage = (message) => {
  return (
    <div className="mb-3 text-sm text-gray-700 dark:text-gray-300">
      <p className="whitespace-pre-wrap">
        {message}
      </p>
    </div>
  );
};

// Alias to the util checker; fallback if util not available
const isCompleteHtmlDocument = (html) => _isCompleteEmailHtml ? _isCompleteEmailHtml(html) : (typeof html === 'string' && (html.trim().toLowerCase().startsWith('<!doctype') || html.trim().toLowerCase().startsWith('<html')));

// Component to render email message with HTML content (uses iframe to preserve backend CSS)
const EmailMessage = ({ message, subject, attachments }) => {
  const [showFullView, setShowFullView] = useState(false);

  // Check if it's a complete HTML document
  const isComplete = isCompleteHtmlDocument(message);

  // Extract plain text from HTML for preview
  const getPlainTextPreview = (htmlContent) => {
    if (!htmlContent) return '';

    try {
      // First, remove style tags, script tags, and comments
      let cleanedHtml = htmlContent
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style tags and content
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script tags and content
        .replace(/<!--[\s\S]*?-->/g, '') // Remove HTML comments
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, ''); // Remove head tag and content

      // Add space after block elements to preserve line breaks
      cleanedHtml = cleanedHtml
        .replace(/<\/(p|div|h[1-6]|li|tr|td|th|br)>/gi, '</$1> ')
        .replace(/<br\s*\/?>/gi, ' ');

      // Create a temporary div to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanedHtml;

      // Get text content and clean it up
      const text = tempDiv.textContent || tempDiv.innerText || '';

      // Normalize whitespace: replace multiple spaces/newlines with single space, but preserve readability
      return text.replace(/\s+/g, ' ').trim();
    } catch (error) {
      console.error('Error extracting plain text:', error);
      // Fallback: strip HTML tags with regex
      return htmlContent
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<\/(p|div|h[1-6]|li|tr|td|th|br)>/gi, '</$1> ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
  };

  const plainTextPreview = getPlainTextPreview(message);

  return (
    <>
      <div className="mb-3">
        {/* Subject line if available */}
        {subject && (
          <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate">
              Subject: <span className="text-gray-700 dark:text-gray-300 font-normal">{subject}</span>
            </p>
          </div>
        )}

        {/* Email content preview as plain text with View Full Email button on right */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 text-sm text-gray-700 dark:text-gray-300">
            <p className="line-clamp-2 whitespace-pre-wrap">
              {plainTextPreview || 'No content'}
            </p>
          </div>

          {/* View full email button */}
          {message && (
            <button
              onClick={() => setShowFullView(true)}
              className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors flex items-center gap-1 mt-0.5"
            >
              <EyeIcon className="h-3 w-3" />
              View Full Email
            </button>
          )}
        </div>

        {/* Email Attachments */}
        {attachments && attachments.length > 0 && (
          <EmailAttachments attachments={attachments} />
        )}
      </div>

      {/* Full Email Modal with iframe */}
      {showFullView && (
        <Transition appear show={showFullView} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-[100] overflow-y-auto"
            onClose={() => { }}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all dark:bg-gray-900">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                      <div className="flex items-center gap-3">
                        <EnvelopeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        <div>
                          <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white">
                            Email Preview
                          </DialogTitle>
                          {subject && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Subject: {subject}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setShowFullView(false)}
                        className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Email Content in iframe - use full version, not preview version */}
                    <div className="p-6 bg-gray-50 dark:bg-gray-800">
                      <iframe
                        title="email-full-preview"
                        srcDoc={isComplete ? message : ensureCompleteEmailHtml(message || "", false)}
                        sandbox="allow-same-origin"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-white shadow-sm"
                        style={{ height: 'calc(80vh - 150px)', minHeight: '400px' }}
                      />
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
                      <Button
                        onClick={() => setShowFullView(false)}
                        variant="outlined"
                      >
                        Close
                      </Button>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
};

const CommunicationsTab = ({ leadId, isMobile, initialChannelId = 0 }) => {
  // State management
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialChannelId);
  const [channelId, setChannelId] = useState(initialChannelId);

  // Sync state if initialChannelId prop changes (for deep linking)
  useEffect(() => {
    if (initialChannelId !== undefined) {
      setActiveTab(initialChannelId);
      setChannelId(initialChannelId);
    }
  }, [initialChannelId]);

  // Refs
  const scrollElementId = `communications-scroll-${leadId}`;
  const isFetchingRef = useRef(false);
  const nextPageRef = useRef(1);

  const PAGE_SIZE = 5; // Number of communications per API call

  // Function to get channel icon and color
  const getChannelStyle = (channelId) => {
    switch (channelId) {
      case 1: // EMAIL
        return { icon: EnvelopeIcon, color: "primary", label: "Email" };
      case 2: // SMS
        return { icon: PaperAirplaneIcon, color: "info", label: "SMS" };
      case 3: // WHATSAPP
        return {
          icon: ChatBubbleLeftRightIcon,
          color: "success",
          label: "WhatsApp",
        };
      case 4: // CALL
        return {
          icon: PhoneIcon,
          color: "indigo",
          label: "Call",
        };
      default:
        return {
          icon: InformationCircleIcon,
          color: "neutral",
          label: "Unknown",
        };
    }
  };



  // Function to get delivery direction label
  const getDeliveryDirection = (direction) => {
    return direction === 1 ? "Sent" : "Received";
  };
  // Fetch communication timeline data
  const fetchCommunications = useCallback(
    async (page = 1, append = false) => {
      if (!leadId) return;
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      if (!append) setLoading(true);
      setError(null);

      try {
        const response = await getCommunicationTimeline(
          leadId,
          channelId === 0 ? null : channelId,
          page,
          PAGE_SIZE,
        );

        if (response.success) {
          const newCommunications = response.data || [];
          const total = response.totalRecords || 0;

          setCommunications((prev) => {
            const nextList = append
              ? [...prev, ...newCommunications]
              : newCommunications;

            // Prevent duplicate entries
            const uniqueCommunications = nextList.filter(
              (comm, index, self) =>
                index === self.findIndex((c) => c.LogId === comm.LogId),
            );

            // Update hasMore based on unique communications
            const more =
              uniqueCommunications.length < total &&
              newCommunications.length > 0;
            setHasMore(more);
            setTotalRecords(total);

            return uniqueCommunications;
          });

          if (append && newCommunications.length > 0) {
            const newPageNumber = page + 1;
            nextPageRef.current = newPageNumber;
            setPageNumber(newPageNumber);
          } else if (!append) {
            nextPageRef.current = 2;
            setPageNumber(2);
          }

          // Stop loading if no new data
          if (append && newCommunications.length === 0) {
            setHasMore(false);
          }
        } else {
          setError(response.error || "Failed to fetch communications");
          setHasMore(false);
        }
      } catch (err) {
        setError("Failed to fetch communication data");
        setHasMore(false);
        console.error("Communication fetch error:", err);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [leadId, channelId],
  );

  // Initial data fetch
  useEffect(() => {
    if (leadId) {
      setPageNumber(1);
      nextPageRef.current = 1;
      setCommunications([]);
      setHasMore(true);
      setTotalRecords(0);
      setError(null);
      fetchCommunications(1, false);
    }
  }, [leadId, channelId, fetchCommunications]);

  // Load more data function
  const loadMoreData = useCallback(() => {
    if (!hasMore || isFetchingRef.current) return;
    fetchCommunications(nextPageRef.current, true);
  }, [hasMore, fetchCommunications]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab.id);
    setChannelId(tab.channelId);
  };

  // Manual refresh function
  const handleRefresh = () => {
    setCommunications([]);
    setPageNumber(1);
    nextPageRef.current = 1;
    setHasMore(true);
    setTotalRecords(0);
    setError(null);
    fetchCommunications(1, false);
  };

  // Sort communications by date (newest first)
  const sortedCommunications = communications.sort(
    (a, b) => getLocalDateTime(b.SentAt || b.CreatedAt) - getLocalDateTime(a.SentAt || a.CreatedAt),
  );

  return (
    <Card
      className={`${isMobile ? "p-3" : "p-4"} flex h-full flex-col`}
    >
      {/* Channel tabs with refresh button */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-1">
          <nav
            className="-mb-px flex space-x-2 overflow-x-auto flex-1"
            aria-label="Tabs"
          >
            {CHANNEL_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab)}
                  className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap ${activeTab === tab.id
                    ? "border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    } ${isMobile ? "text-xs" : ""} `}
                >
                  <Icon className="h-4 w-4" />
                  {tab.name}
                  {tab.id === 0 && totalRecords > 0 && (
                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                      ({totalRecords})
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="h-8 w-8 p-0 ml-2"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Scrollable Content - Increased height */}
      <div className="flex-1 overflow-hidden">
        <div
          id={scrollElementId}
          className="h-full overflow-auto px-0 py-3"
          style={{ height: "calc(100vh - 14rem)" }}
        >
          {/* Loading state removed - handled by global loader */}
          {sortedCommunications.length > 0 ? (
            <InfiniteScroll
              dataLength={sortedCommunications.length}
              next={loadMoreData}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center py-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <div className="border-primary-500 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                    <span>Loading more communications...</span>
                  </div>
                </div>
              }
              endMessage={
                <div className="py-6 text-center">
                  <div className="inline-flex items-center gap-2 rounded-xl bg-gray-50 px-4 py-2 text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">
                      No more communications to load
                    </span>
                  </div>
                </div>
              }
              scrollableTarget={scrollElementId}
              className="h-full"
            >
              <div className="space-y-4">
                {sortedCommunications.map((communication) => {
                  const {
                    icon: ChannelIcon,
                    color: channelColor,
                    label: channelLabel,
                  } = getChannelStyle(communication.ChannelId);
                  const statusInfo =
                    COMMUNICATION_STATUS[communication.Status] ||
                    COMMUNICATION_STATUS[0];
                  const StatusIcon = statusInfo.icon;

                  // Determine if message is outgoing (sent) or incoming (received)
                  const isOutgoing = communication.DeliveryDirection === 1;
                  const isIncoming = communication.DeliveryDirection === 2;

                  return (
                    <div
                      key={communication.LogId}
                      className={`rounded-lg border p-4 shadow-sm ${isOutgoing
                        ? 'border-green-200 bg-gradient-to-r from-green-50/50 to-white dark:border-green-800/50 dark:from-green-900/10 dark:to-gray-800'
                        : 'border-blue-200 bg-gradient-to-r from-blue-50/50 to-white dark:border-blue-800/50 dark:from-blue-900/10 dark:to-gray-800'
                        }`}
                    >
                      {/* Header */}
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div
                            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${isOutgoing
                              ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-200 dark:ring-green-800/50'
                              : 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-200 dark:ring-blue-800/50'
                              }`}
                          >
                            <ChannelIcon
                              className={`h-5 w-5 ${isOutgoing
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-blue-600 dark:text-blue-400'
                                }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
                              {/* For WhatsApp media messages, show "WhatsApp Communication" instead of Subject */}
                              {(communication.IsMediaMessage === true || communication.IsMediaMessage === 1) && communication.ChannelId === 3
                                ? "WhatsApp Communication"
                                : communication.Subject || `${channelLabel} Communication`}
                            </div>
                            <div className="text-xs flex items-center gap-1.5">
                              <span className={`font-medium ${isOutgoing
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-blue-600 dark:text-blue-400'
                                }`}>
                                {channelLabel}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Status Badge - Show direction arrow with status */}
                          <Tag
                            variant="soft"
                            color={statusInfo.color}
                            className="text-xs flex-shrink-0"
                          >
                            {/* For successful status (2), show Sent/Received. For others, show status label */}
                            {communication.Status === 2 ? (
                              // Status 2 = Sent/Completed - show direction arrow with Sent/Received
                              <>{isOutgoing ? '↑ Sent' : '↓ Received'}</>
                            ) : (
                              // Failed, Pending, etc. - show direction arrow with status label
                              <>{isOutgoing ? '↑' : '↓'} {statusInfo.label}</>
                            )}
                          </Tag>
                        </div>
                      </div>

                      {/* Message content - Dynamic rendering based on type */}
                      {(() => {
                        // Check if it's a Call message (ChannelId 4)
                        if (communication.ChannelId === 4) {
                          return <CallMessage key={communication.LogId} communication={communication} />;
                        }

                        // Check if it's a media message
                        if (communication.IsMediaMessage === true || communication.IsMediaMessage === 1) {
                          return <MediaMessage key={communication.LogId} communication={communication} />;
                        }

                        // Check if it's a WhatsApp template message
                        if (communication.IsWhatsappTemplate === 1 || communication.IsWhatsappTemplate === true) {
                          return renderTemplateMessage(communication);
                        }

                        // Check if it's an Email message (ChannelId === 1) - render with HTML
                        if (communication.ChannelId === 1 && communication.Message) {
                          return <EmailMessage message={communication.Message} subject={communication.Subject} attachments={communication.Attachments} />;
                        }

                        // Default: render as normal text message
                        if (communication.Message) {
                          return renderTextMessage(communication.Message);
                        }

                        return null;
                      })()}

                      {/* Footer */}
                      <div className="flex flex-col gap-2 text-xs sm:flex-row sm:items-center sm:justify-between text-gray-500 dark:text-gray-400">
                        {/* Left section */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                          <span className="max-w-[200px] truncate sm:max-w-none">
                            {isOutgoing ? 'To:' : 'From:'} {communication.Recipient}
                          </span>
                        </div>

                        {/* Right section */}
                        <div className="flex items-center gap-1">
                          <span>{formatDisplayDate(communication.SentAt || communication.CreatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </InfiniteScroll>
          ) : (
            !loading && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    No Communications
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    No communications found for this lead yet.
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </Card>
  );
};

export default CommunicationsTab;
